import React, { useEffect, useState } from 'react'
import {
    CurrentBrowser,
    getDomainLinkingStatus,
    selectIsExtensionInstalled,
    selectIsLoggedInToADomain,
    selectLoggedInDomains,
    selectLoggedOutDomains,
    selectSetUpRequiredDomains,
    setDomainLoginStatus,
    setLastSyncedAt,
    setSyntheticLinkedAt,
    useAppDispatch,
    useAppSelector
} from '@foxtail-dev/user-clients'
import { Box, Divider } from '@mui/material'
import { FoxTypography } from '../../components/common/FoxTypography'
import { LinkedMarketplaceRow } from '../../containers/account/LinkedMarketplaceRow'
import { ConnectAMarketplaceRow } from '../../containers/account/ConnectAMarketplaceRow'
import { AccountHeader } from '../../layouts/AccountHeader'
import { EbayInventoryLocationModal } from '../../modals/account/marketConnections/EbayInventoryLocationModal'
import { MustUseChromeToLinkMarketPlaces } from '../../components/account/MustUseChromeToLinkMarketPlaces'
import { ChromeExtensionNotInstalledMessage } from '../../components/account/ChromeExtensionNotInstalledMessage'
import { ExtensionReloadModal } from '../../modals/account/marketConnections/ExtensionReloadModal'
import { DomainCommonName } from '@foxtail-dev/datacontracts'

const MarketConnectionsScreen: React.FC = () => {
    const dispatch = useAppDispatch()
    const isLoggedIntoADomain = useAppSelector(selectIsLoggedInToADomain)
    const syntheticLinkedTimestamp = useAppSelector((state) => state.domainAuth.syntheticLinkedTimestampMap)
    const loggedInDomains = useAppSelector(selectLoggedInDomains)
    const setupRequiredDomains = useAppSelector(selectSetUpRequiredDomains)
    const loggedOutDomains = useAppSelector(selectLoggedOutDomains)
    const isChromeExtensionInstalled = useAppSelector(selectIsExtensionInstalled)
    const [ebayInventoryLocationModal, setEbayInventoryLocationModalOpen] = useState<boolean>(false)
    const [isUsingChromeBrowser, setIsUsingChromeBrowser] = useState<boolean>(true)
    const [extensionReloadModalOpen, setExtensionReloadModalOpen] = useState<boolean>(false)

    // get transferredAuthFromDomain query param from url
    const urlParams = new URLSearchParams(window.location.search)
    const transferredAuthFromDomain = urlParams.get('transferredAuthFromDomain')

    useEffect(() => {
        if (transferredAuthFromDomain) {
            const domainName = DomainCommonName.parse(transferredAuthFromDomain)
            dispatch(setLastSyncedAt(domainName))
            dispatch(setSyntheticLinkedAt(domainName))
            dispatch(setDomainLoginStatus({ domainName, loginStatus: 'Logged In' }))

            const url = new URL(window.location.href)
            url.searchParams.delete('transferredAuthFromDomain')
            window.history.replaceState({}, document.title, url.pathname + url.search)
        }
    }, [transferredAuthFromDomain])

    const closeExtensionReloadModal = (result: boolean) => {
        setExtensionReloadModalOpen(false)
    }
    useEffect(() => {
        const isChromeBrowser = CurrentBrowser.getBrowserKind() === 'Chrome'
        setIsUsingChromeBrowser(isChromeBrowser)
    }, [])

    useEffect(() => {
        dispatch(getDomainLinkingStatus())

        const interval = setInterval(() => {
            dispatch(getDomainLinkingStatus())
        }, 5000)
        return () => clearInterval(interval)
    }, [])

    const closeEbayInventoryLocationModal = () => {
        setEbayInventoryLocationModalOpen(false)
    }

    const showMarkets = isLoggedIntoADomain || isChromeExtensionInstalled

    return (
        <Box sx={styles.container}>
            <AccountHeader title='Marketplace connections' hideDivider={isChromeExtensionInstalled === false} />

            {isUsingChromeBrowser === false ? (
                <MustUseChromeToLinkMarketPlaces />
            ) : (
                <Box>
                    {showMarkets ? (
                        <>
                            {loggedInDomains.length > 0 && (
                                <Box sx={{ marginBottom: '32px' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginLeft: '32px' }}>
                                        <FoxTypography sx={{ marginBottom: '12px' }} variant='subtitle1'>
                                            Linked accounts
                                        </FoxTypography>
                                    </Box>
                                    <Divider sx={{ marginLeft: '31px' }} />
                                    <Box>
                                        {loggedInDomains.map((domain: DomainCommonName) => (
                                            <LinkedMarketplaceRow
                                                key={domain}
                                                domainName={domain}
                                                status='Logged In'
                                                setEbayInventoryLocationModalOpen={setEbayInventoryLocationModalOpen}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {[...setupRequiredDomains, ...loggedOutDomains].length > 0 && (
                                <Box sx={{ marginLeft: '32px', marginBottom: '11px' }}>
                                    <FoxTypography sx={{ marginBottom: '11px' }} variant='subtitle1'>
                                        Connect a marketplace
                                    </FoxTypography>
                                    <Divider />
                                </Box>
                            )}
                            <Box>
                                {setupRequiredDomains.map((domain) => (
                                    <ConnectAMarketplaceRow
                                        key={domain}
                                        domainName={domain}
                                        setupRequired={true}
                                        setEbayInventoryLocationModalOpen={setEbayInventoryLocationModalOpen}
                                    />
                                ))}
                                {loggedOutDomains.map((domain) => (
                                    <ConnectAMarketplaceRow
                                        key={domain}
                                        domainName={domain}
                                        setupRequired={false}
                                        setEbayInventoryLocationModalOpen={setEbayInventoryLocationModalOpen}
                                    />
                                ))}
                            </Box>
                        </>
                    ) : (
                        <ChromeExtensionNotInstalledMessage setExtensionReloadModalOpen={setExtensionReloadModalOpen} />
                    )}
                </Box>
            )}

            <EbayInventoryLocationModal open={ebayInventoryLocationModal} onClose={closeEbayInventoryLocationModal} />
            <ExtensionReloadModal open={extensionReloadModalOpen} onClose={closeExtensionReloadModal} />
        </Box>
    )
}

const styles = {
    container: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        marginRight: '260px'
    }
}

export default MarketConnectionsScreen
